import { isNullOrWhitespace } from "../../../../shared/utils/string";
import { flattenAllCollectionsWithParents } from "./collection-methods";
import {
    type MappedProductCollectionModel,
    type ProductCollectionModel,
} from "./models";
import {
    collectionIdKeyword,
    collectionIdSeparator,
    collectionIdSetter,
} from "./url";
import { constructMappedItem } from "./view-modes/mapped-items";

interface FlatResultItem {
    collection: ProductCollectionModel;
    path: ProductCollectionModel[];
}

function flatReduceAllSubItemsWithPath(
    models: ProductCollectionModel[],
    extraPath: ProductCollectionModel[] | undefined = undefined
): FlatResultItem[] {
    const allSubGroups = models.reduce((prev, curr) => {
        //if (curr == null)
        //    throw new Error("Unexpected null/undefined");
        prev.push({ collection: curr, path: extraPath ?? [] });
        for (const subItem of flatReduceAllSubItemsWithPath(
            curr.SubCollections,
            (extraPath ?? []).concat([curr])
        )) {
            prev.push(subItem);
        }
        return prev;
    }, [] as FlatResultItem[]);
    return allSubGroups;
}

let logging = false;

export function enableLogging() {
    logging = true;
}

function log(...args: any[]) {
    if (!logging) return;
    console.log(...args);
}

function hasViewMode1(id: number, flat: MappedProductCollectionModel[]) {
    let item = flat.find((x) => x.Id == id);
    while (item != null) {
        if (item.FilterViewMode == 1) {
            return true;
        }
        item = item.parent;
    }
    return false;
}

export function getCollectionUrl(
    item: MappedProductCollectionModel,
    viewMode: number,
    baseUrl: string | undefined,
    value: number[][] | undefined,
    root: ProductCollectionModel[],
    matchedLogicalName?: string | undefined,
    matchedLogicalNameEntityGuid?: number,
    matchedLogicalNameModuleGuid?: number,
    matchedLogicalNameSubModuleGuid?: number
) {
    if (item == null) {
        log("returning base url since item is null");
        return baseUrl;
    }
    if (item.Filler == true) {
        return baseUrl;
    }
    if (item.LogicalName == null || isNullOrWhitespace(item.LogicalName)) {
        log(
            "returning null since item has no logical name (null or empty/whitespace)"
        );
        console.error("A collection item is missing a logicalName", item);
        return null;
    }
    if (
        viewMode == 1 &&
        value != null &&
        value.length > 0 &&
        !root.some((x) => x.Id == item.Id)
    ) {
        log("entering viewMode:1");
        const flat = root.map((x) => constructMappedItem(x, undefined));
        const linkToValue = value
            .map((x) => x.filter((y) => hasViewMode1(y, flat)))
            .concat();

        let rootParent = item;
        while (rootParent.parent != null) {
            rootParent = rootParent.parent;
        }
        const rootItem = root.find((r) => r.Id == rootParent.Id);
        if (rootItem == null) throw new Error("Could not find root item");
        const rootPos = root.indexOf(rootItem);

        while (linkToValue.length < rootPos + 1) {
            linkToValue.push([]);
        }

        linkToValue[rootPos].push(item.Id);

        let url = baseUrl;

        for (const group of linkToValue) {
            if (group.length == 0) continue;

            if (group.length === 1) {
                const collectionId = group[0];

                const set = Array.from(
                    flattenAllCollectionsWithParents(root)
                ).find((set) => set.collection.Id == collectionId);
                if (set == null) {
                    throw new Error(
                        "did not find collectionId " +
                            collectionId +
                            " anywhere"
                    );
                }
                const groupRootItem = set.parents[0];
                url += groupRootItem.LogicalName;

                try {
                    const allSubGroups = flatReduceAllSubItemsWithPath(
                        groupRootItem.SubCollections
                    );
                    const matchRelativePath = getUrlPartForCollectionId(
                        allSubGroups,
                        collectionId
                    );

                    url += matchRelativePath;

                    continue;
                } catch (err) {
                    if ((err as any).code == "not_found") {
                        throw new Error(
                            "did not find collectionId " +
                                collectionId +
                                " under collection " +
                                JSON.stringify(groupRootItem.Name)
                        );
                    }
                    throw err;
                }
            } else {
                const firstCollectionId = group[0];
                const set = Array.from(
                    flattenAllCollectionsWithParents(root)
                ).find((set) => set.collection.Id == firstCollectionId);
                if (set == null) {
                    throw new Error(
                        "did not find collectionId " +
                            firstCollectionId +
                            " anywhere"
                    );
                }
                const groupRootItem = set.parents[0];
                if (groupRootItem != null) {
                    url += groupRootItem.LogicalName;
                }
            }

            const urlPart = group
                .map((x) => collectionIdKeyword + collectionIdSetter + x)
                .join(collectionIdSeparator);
            url += "/" + urlPart;
        }

        return url;
    }
    log("not viewMode 1");
    let url = item.LogicalName;
    let parent = item.parent;
    log("url is now: " + url);
    while (parent != null) {
        url = parent.LogicalName + url;
        parent = parent.parent;
        log("url is now: " + url);
    }
    if (
        baseUrl == matchedLogicalName &&
        matchedLogicalNameModuleGuid === 392 &&
        matchedLogicalNameSubModuleGuid === 0
    ) {
        // baseUrl is the current page
    } else {
        url = baseUrl + url;
    }

    return url;
}

function getUrlPartForCollectionId(
    allSubGroups: FlatResultItem[],
    collectionId: number
) {
    const match = allSubGroups.find((gr) => gr.collection.Id == collectionId);
    if (match == null) {
        console.error(
            "collection with id " +
                collectionId +
                " does not exist in collection",
            allSubGroups
        );
        const err = new Error(
            "Could not find match for single item with id " + collectionId
        );
        (err as any).code = "not_found";
        throw err;
    }
    let matchRelativePath = match.collection.LogicalName;

    if (match.path.length > 0) {
        const reversed = match.path.reverse();
        for (const pathItem of reversed) {
            matchRelativePath = pathItem.LogicalName + matchRelativePath;
        }
    }
    return matchRelativePath;
}
